import React, { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { ListChecks, Sparkles, FileText, ArrowRight, User, CheckCircle2 } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'

const R = (n) => 'R' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }) : '—'

export default function TreatmentSteps({ patients = [], selected = null, patient = null, estimates = [], flash_generated = null }) {
  const [steps, setSteps] = useState('')
  const [busy, setBusy] = useState(false)
  const generatedIds = new Set(flash_generated || [])

  const pickPatient = (id) => router.get('/treatment-steps', id ? { patient_id: id } : {}, { preserveScroll: true })

  const generate = () => {
    if (!steps.trim()) return
    setBusy(true)
    router.post('/treatment-steps/generate', { patient_id: selected, steps }, {
      onFinish: () => { setBusy(false); setSteps('') },
    })
  }

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary"><ListChecks size={18} className="text-white" /></div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Treatment steps</h1>
          <p className="text-sm text-brand-muted">Type the plan as numbered shorthand — each step becomes its own estimate. Accepting an estimate never bills.</p>
        </div>
      </div>

      {/* Patient picker */}
      <div className="mb-4 rounded-xl border border-brand-border bg-white p-4 shadow-sm">
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brand-muted">Patient</label>
        <div className="flex items-center gap-2">
          <User size={16} className="text-brand-muted" />
          <select
            value={selected || ''}
            onChange={(e) => pickPatient(e.target.value)}
            className="w-full max-w-md rounded-lg border border-brand-border px-3 py-2 text-sm"
          >
            <option value="">— select a recent patient —</option>
            {patients.map((p) => <option key={p.id} value={p.id}>{p.name}{p.account ? ` · ${p.account}` : ''}</option>)}
          </select>
        </div>
      </div>

      {!patient && (
        <div className="rounded-xl border border-dashed border-brand-border bg-white p-8 text-center text-sm text-brand-muted">
          Pick a patient above to start building their treatment-step estimates.
        </div>
      )}

      {patient && (
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Steps entry */}
          <div className="rounded-xl border border-brand-border bg-white p-4 shadow-sm">
            <h2 className="mb-1 text-sm font-semibold text-brand-ink">{patient.name}{patient.account ? ` · ${patient.account}` : ''}{patient.scheme ? ` · ${patient.scheme}` : ''}</h2>
            <p className="mb-2 text-xs text-brand-muted">One step per line, in shorthand (English/Afrikaans). Each line → its own estimate.</p>
            <textarea
              value={steps}
              onChange={(e) => setSteps(e.target.value)}
              placeholder={"C/U & clean\nXLA 22\nFill post 46 MOD\nCrown 46\nChrome cobalt upper\nRCT 46"}
              spellCheck={false}
              className="h-44 w-full rounded-lg border border-brand-border p-3 font-mono text-sm"
            />
            <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-brand-muted">
              {['Crown 16', 'XLA 22', 'Fill post 46 MOD', 'RCT 46', 'Chrome cobalt upper', 'Post & core 23', 'kroon 11'].map((ex) => (
                <button key={ex} type="button" onClick={() => setSteps((s) => (s ? s + '\n' : '') + ex)} className="rounded border border-brand-border bg-brand-surface px-1.5 py-0.5 hover:bg-white">{ex}</button>
              ))}
            </div>
            <button
              onClick={generate}
              disabled={busy || !steps.trim()}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-primary/90 disabled:opacity-50"
            >
              <Sparkles size={15} /> {busy ? 'Generating…' : 'Generate per-step estimates'}
            </button>
            <p className="mt-2 text-[11px] text-brand-muted">Recognised shorthand is coded instantly from the practice macros; anything unrecognised is flagged on the estimate (never dropped).</p>
          </div>

          {/* Estimates for this patient */}
          <div className="rounded-xl border border-brand-border bg-white p-4 shadow-sm">
            <h2 className="mb-2 text-sm font-semibold text-brand-ink">Estimates for {patient.name}</h2>
            {estimates.length === 0 && <p className="text-sm text-brand-muted">No estimates yet — generate the steps on the left.</p>}
            <div className="space-y-2">
              {estimates.map((e) => (
                <Link
                  key={e.id}
                  href={`/estimates/${e.id}`}
                  className={`block rounded-lg border p-3 transition hover:border-brand-primary ${generatedIds.has(e.id) ? 'border-emerald-300 bg-emerald-50' : 'border-brand-border bg-white'}`}
                >
                  <div className="flex items-center gap-2">
                    {generatedIds.has(e.id) ? <CheckCircle2 size={15} className="text-emerald-600" /> : <FileText size={15} className="text-brand-muted" />}
                    <span className="flex-1 truncate text-sm font-medium text-brand-ink">{e.title || e.number}</span>
                    <span className="text-xs text-brand-muted">{e.lines} line{e.lines === 1 ? '' : 's'}</span>
                    <span className="text-sm font-semibold text-brand-ink">{R(e.total)}</span>
                    <ArrowRight size={14} className="text-brand-muted" />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-brand-muted">
                    <span className="font-mono">{e.number}</span>
                    <span className={`rounded px-1.5 py-0.5 font-medium ${e.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{e.status}</span>
                    <span>{fmtDate(e.date)}</span>
                  </div>
                  {e.ai_note && <p className="mt-1 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800">⚠ {e.ai_note}</p>}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
